<script setup>
import Api from '@/services/api/ApiServiceFabrick';
import AuthorityCacheApi from '@/services/models/AuthorityCacheApi';
import ManagementOfficeAutoComplete from '@/components/AutoCompletes/ManagementOfficeAutoComplete';
import SingleOperationOfficeAutoComplete from '@/components/AutoCompletes/SingleOperationOfficeAutoComplete';
import SingleDeviceTypeAutoComplete from '@/components/AutoCompletes/SingleDeviceTypeAutoComplete';
import { useAuth } from '@/services/user/Auth0UserProvider';
import { useRoute, useRouter } from 'vue-router';
import { reactive, ref, onMounted, nextTick, onBeforeUnmount, computed } from 'vue';
import i18n from '@/lang/i18n';
import { IMEI_REQUIRED_IDS, TELTONIKA_MAKER_ID } from '@/services/consts';

const props = defineProps({
  showFlash: { type: Function },
  switchContentLoading: { type: Function },
});

const loginWithRedirect = ref(null);
const router = useRouter();
const route = useRoute();
const title = ref('');
const showableUpdateButton = ref(false);
const data = reactive({
  management_office: undefined,
  operation_office: undefined,
  device: {},
  errors: {},
});
const initialManagementId = ref();
const initialManagementType = ref();
const initialOperationId = ref();
const initialDeviceId = ref();
const isUpdating = ref(false);
const updatable = ref(false);
const deviceId = ref(null);
const makers = ref([]);
const windowInnerWidth = ref(window.innerWidth);
const isRequiredImei = computed(() =>
  IMEI_REQUIRED_IDS.includes(data.device.model_type?.id)
);
// メーカーが TELTONIKA なら型番は必須。プロビジョニング実行時に422になるのを事前に防ぐ
const isTeltonika = computed(() => data.device?.makerId == TELTONIKA_MAKER_ID);

(async () => {
  props.switchContentLoading(true);
  deviceId.value = route.params.id;
  title.value = deviceId.value ?
    i18n.global.t('pages.devices.title.edit') :
    i18n.global.t('pages.devices.title.create');
  document.title = title.value;

  AuthorityCacheApi.getWithParse().then((authorityList) => {
    if (deviceId.value) {
      showableUpdateButton.value = !!authorityList['octlink.device.update'];
    } else {
      showableUpdateButton.value = !!authorityList['octlink.device.create'];
    }
    if (!authorityList['octlink.device.read']) {
      router.push('/');
    }
  });
  const { loginWithRedirect } = useAuth();
  loginWithRedirect.value = loginWithRedirect;

  if (deviceId.value != '') {
    const response = await Api()
      .getDevice(deviceId.value)
      .catch((error) => {
        if (error.request.status == 403) {
          router.push('/devices');
          return props.showFlash(i18n.global.t('pages.common.messages.forbidden'), 'error', true);
        }
        return errorHandle(error.request);
      });
    if (!response) return;
    updatable.value = true;

    makeInitialParams(response);
  }

  props.switchContentLoading(false);
})();

onMounted(() => {
  if (deviceId.value == '') {
    data.device = {};
    updatable.value = true;
  }
  getAllMakers();
});

function makeInitialParams(response) {
  initialManagementId.value = response.management_officeable_id
  initialManagementType.value = response.management_officeable_type
  initialOperationId.value = response.operation_office_id;
  initialDeviceId.value = response.model_type;
  data.device = response;
  data.device.makerId = response.maker.id;
}

function getAllMakers() {
  Api()
    .getMakers()
    .then((res) => {
      makers.value = res;
    })
    .catch((error) => {
      return errorHandle(error.response);
    });
}

function validateForm() {
  data.errors = {};
  if (isTeltonika.value && !data.device.model_type?.id) {
    data.errors = { model_type: [i18n.global.t('pages.devices.form.modelTypeRequiredForTeltonika')] };
    scrollToErrorComponent(data.errors);
    return false;
  }
  return true;
}

function submit() {
  if (!validateForm()) {
    return;
  }
  isUpdating.value = true;
  const requestParams = makeRequestParams();
  Api()
    .createOrUpdateDevice(deviceId.value, requestParams)
    .then(() => {
      success();
    })
    .catch((error) => {
      errorHandle(error.response);
    });
}

function makeRequestParams() {
  const requestParams = {
    id: deviceId.value,
    maker_id: data.device?.makerId,
    model_number: data.device?.model_number,
    device_id: data.device?.device_id,
    sim_id: data.device?.sim_id,
    operation_office_id: data.operation_office?.id,
    management_officeable_id: data.management_office?.id,
    management_officeable_type: data.management_office?.type,
    status: 1,
    imei: data.device.imei,
    model_type: data.device.model_type?.id,
  };

  return requestParams;
}

function success() {
  isUpdating.value = false;
  router.push('/devices');
  props.showFlash(i18n.global.t('pages.common.messages.success'), 'success', true);
}

function errorHandle(res) {
  switch (res.status) {
    case 401:
      loginWithRedirect({ appState: { targetUrl: location.pathname } });
      updatable.value = false;
      break;
    case 403:
      props.showFlash(i18n.global.t('pages.common.messages.notEditAuthority'), 'error', true);
      showableUpdateButton.value = false;
      updatable.value = false;
      break;
    case 422:
      data.errors = res.data.errors;
      scrollToErrorComponent(data.errors);
  }
  return (isUpdating.value = false);
}

function scrollToErrorComponent(errors) {
  if (!Object.keys(errors).length) return;
  nextTick(() => {
    // Vuetify の入力を先に探す。無ければ従来の validation-error を見る
    const firstErrorDom =
      document.querySelector('.v-input--error') ??
      document.getElementsByClassName('validation-error')[0]?.parentElement;
    firstErrorDom?.scrollIntoView(true);
  });
}

function handleResize() {
  windowInnerWidth.value = window.innerWidth;
}

window.addEventListener('resize', handleResize);

onBeforeUnmount(() => {
  props.switchContentLoading(false);
});
</script>
<template>
  <div style="min-height: 45vw">
    <h4 class="content pl-3 w-100">{{ title }}</h4>
    <div class="row mr-3 ml-3 v-theme--light">
      <div class="mb-4">
        <v-row class="text-form pl-4 pr-3 mb-3">
          <v-col :cols="windowInnerWidth > 800 ? 2 : 3" class="my-auto">
            <h6 class="pl-4 mb-0">
              {{ i18n.global.t('pages.devices.maker') }}<span style="color: red">✴︎</span>
            </h6>
          </v-col>
          <v-col cols="10">
            <v-select
              class="pt-2"
              v-model="data.device.makerId"
              :items="makers"
              item-title="name"
              item-value="id"
              outlined
              :error-messages="data?.errors['maker_id']"
            />
          </v-col>
        </v-row>
        <v-row class="text-form pl-4 pr-3 mb-3">
          <v-col :cols="windowInnerWidth > 800 ? 2 : 3" class="my-auto">
            <h6 class="pl-4 mb-0">
              {{ i18n.global.t('pages.devices.modelNumber') }}<span style="color: red">✴︎</span>
            </h6>
          </v-col>
          <v-col cols="10">
            <v-text-field
              class="pt-2"
              v-model="data.device.model_number"
              outlined
              :error-messages="data?.errors['model_number']"
            />
          </v-col>
        </v-row>
        <v-row class="text-form pl-4 pr-3 mb-3">
          <v-col :cols="windowInnerWidth > 800 ? 2 : 3" class="my-auto">
            <h6 class="pl-4 mb-0">
              {{ i18n.global.t('pages.devices.deviceId') }}<span style="color: red">✴︎</span>
            </h6>
          </v-col>
          <v-col cols="10">
            <v-text-field
              class="pt-2"
              v-model="data.device.device_id"
              outlined
              :error-messages="data?.errors['device_id']"
            />
          </v-col>
        </v-row>
        <v-row class="text-form pl-4 pr-3 mb-3">
          <v-col :cols="windowInnerWidth > 800 ? 2 : 3" class="my-auto">
            <h6 class="pl-4 mb-0">
              {{ i18n.global.t('pages.devices.simId') }}<span style="color: red">✴︎</span>
            </h6>
          </v-col>
          <v-col cols="10">
            <v-text-field
              class="pt-2"
              v-model="data.device.sim_id"
              outlined
              :error-messages="data?.errors['sim_id']"
            />
          </v-col>
        </v-row>
        <v-row class="text-form pl-4 pr-3 mb-3">
          <v-col :cols="windowInnerWidth > 800 ? 2 : 3" class="my-auto">
            <h6 class="pl-4 mb-0">
              {{ i18n.global.t('pages.devices.imei') }}<span v-if="isRequiredImei" style="color: red">✴︎</span>
            </h6>
          </v-col>
          <v-col cols="10">
            <v-text-field
              class="pt-2"
              v-model="data.device.imei"
              outlined
              :error-messages="data?.errors['imei']"
            />
          </v-col>
        </v-row>
        <v-row class="text-form pl-4 pr-3 mb-3">
          <v-col :cols="windowInnerWidth > 800 ? 2 : 3" class="my-auto">
            <h6 class="pl-4 mb-0">
              {{ i18n.global.t('pages.devices.modelType') }}<span v-if="isTeltonika" style="color: red">✴︎</span>
            </h6>
          </v-col>
          <v-col cols="10">
            <SingleDeviceTypeAutoComplete
              v-model="data.device.model_type"
              :initialItemId="initialDeviceId"
              :makerId="data.device.makerId"
              :error-messages="data?.errors['model_type']"
            />
          </v-col>
        </v-row>
        <v-row class="text-form pl-4 pr-3 mb-3">
          <v-col :cols="windowInnerWidth > 800 ? 2 : 3" class="my-auto">
            <h6 class="pl-4 mb-0">
              {{ i18n.global.t('pages.devices.managementOffice') }}<span style="color: red">✴︎</span>
            </h6>
          </v-col>
          <v-col cols="10">
            <ManagementOfficeAutoComplete
              v-model="data.management_office"
              :errorMessages="data?.errors['management_officeable_id']"
              :initialManagementId="initialManagementId"
              :initialManagementType="initialManagementType"
              selectFirstIfOnlyOne
            />
          </v-col>
        </v-row>
        <v-row class="text-form pl-4 pr-3 mb-3">
          <v-col :cols="windowInnerWidth > 800 ? 2 : 3" class="my-auto">
            <h6 class="pl-4 mb-0">
              {{ i18n.global.t('pages.devices.operationOffice') }}<span style="color: red">✴︎</span>
            </h6>
          </v-col>
          <v-col cols="10">
            <SingleOperationOfficeAutoComplete
              v-model="data.operation_office"
              :initialOperationId="initialOperationId"
              :managementOffice="data.management_office"
              :errorMessages="data?.errors['operation_office_id']"
              selectFirstIfOnlyOne
            />
          </v-col>
        </v-row>
        <div
          v-if="updatable && showableUpdateButton"
          class="form-footer p-2"
        >
          <router-link :to="{ name: 'DeviceList' }">
            <v-btn class="ma-2 reset-btn">
              {{ i18n.global.t('common.cancelBtn') }}
            </v-btn>
          </router-link>
          <v-btn class="ma-2 exec-btn" @click="submit" :loading="isUpdating">
            {{ i18n.global.t('pages.common.button.conservation') }}
          </v-btn>
        </div>
      </div>
    </div>
  </div>
</template>
